import { useId, useImperativeHandle, useRef, useState } from 'react';
import { Bold, Eye, Heading2, Italic, Link2, List, Pilcrow, SquarePen } from 'lucide-react';
import { Button } from './button.jsx';
import { Textarea } from './input.jsx';
import { HtmlFrame } from './html-frame.jsx';
import { cn } from '../lib/utils.js';

/**
 * Inserts `before` + selection + `after` at the cursor of a textarea and returns the new value and caret.
 * @param {HTMLTextAreaElement|HTMLInputElement} el @param {string} before @param {string} [after]
 */
export function insertAround(el, before, after = '') {
  const value = el.value ?? '';
  const start = el.selectionStart ?? value.length;
  const end = el.selectionEnd ?? start;
  const selected = value.slice(start, end);
  const next = `${value.slice(0, start)}${before}${selected}${after}${value.slice(end)}`;
  const caret = start + before.length + selected.length + (selected ? after.length : 0);
  return { value: next, caret };
}

/**
 * Puts `text` at the cursor of an input/textarea (controlled) and restores the caret afterwards.
 * @param {HTMLTextAreaElement|HTMLInputElement|null|undefined} el @param {string} text @param {(value: string) => void} onChange
 */
export function insertAtCursor(el, text, onChange) {
  if (!el) return;
  const { value, caret } = insertAround(el, text);
  onChange(value);
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange?.(caret, caret);
  });
}

const TOOLS = [
  { id: 'p', label: 'Absatz', icon: Pilcrow, before: '<p>', after: '</p>' },
  { id: 'h', label: 'Zwischenüberschrift', icon: Heading2, before: '<h2>', after: '</h2>' },
  { id: 'b', label: 'Fett', icon: Bold, before: '<strong>', after: '</strong>' },
  { id: 'i', label: 'Kursiv', icon: Italic, before: '<em>', after: '</em>' },
  { id: 'ul', label: 'Aufzählung', icon: List, before: '<ul>\n  <li>', after: '</li>\n</ul>' },
  { id: 'a', label: 'Link', icon: Link2, before: '<a href="https://">', after: '</a>' },
];

/**
 * Simple, safe rich-text input: HTML in a textarea with a small formatting toolbar and a sandboxed preview.
 * Nothing is sanitized here – the backend sanitizes (RichText::sanitize) and is authoritative.
 * @param {{
 *   value: string,
 *   onChange: (value: string) => void,
 *   onBlur?: () => void,
 *   id?: string,
 *   label?: string,
 *   rows?: number,
 *   textareaRef?: import('react').Ref<HTMLTextAreaElement>,
 *   onTextareaFocus?: () => void,
 *   className?: string,
 * } & Record<string, any>} props  further props (aria-*) go to the textarea
 */
export function HtmlEditor({ value, onChange, onBlur, id, label = 'Text', rows = 12, textareaRef, onTextareaFocus, className, ...rest }) {
  const fallbackId = useId();
  const localRef = useRef(/** @type {HTMLTextAreaElement|null} */ (null));
  const [preview, setPreview] = useState(false);

  // Expose the textarea (e.g. for inserting placeholders at the cursor); refreshed on every render (preview toggle).
  useImperativeHandle(textareaRef, () => /** @type {HTMLTextAreaElement} */ (localRef.current));

  function apply(tool) {
    const el = localRef.current;
    if (!el) return;
    const { value: next, caret } = insertAround(el, tool.before, tool.after);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div role="toolbar" aria-label={`Formatierung ${label}`} className="flex flex-wrap items-center gap-0.5 rounded-md border bg-muted/50 p-0.5">
        {TOOLS.map((tool) => {
          const Icon = tool.icon;
          return (
            <Button
              key={tool.id}
              type="button"
              variant="ghost"
              size="icon-sm"
              title={tool.label}
              aria-label={tool.label}
              disabled={preview}
              onClick={() => apply(tool)}
            >
              <Icon aria-hidden="true" />
            </Button>
          );
        })}
        <span className="ml-auto" />
        <Button type="button" variant="ghost" size="sm" aria-pressed={preview} onClick={() => setPreview((v) => !v)}>
          {preview ? <SquarePen aria-hidden="true" /> : <Eye aria-hidden="true" />}
          {preview ? 'Bearbeiten' : 'Vorschau'}
        </Button>
      </div>
      {preview ? (
        <HtmlFrame html={value} title={`Vorschau ${label}`} />
      ) : (
        <Textarea
          ref={localRef}
          id={id ?? fallbackId}
          rows={rows}
          spellCheck
          className="font-mono text-[13px]"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onFocus={onTextareaFocus}
          {...rest}
        />
      )}
    </div>
  );
}
